/**
 * Sales Performance round 2, stage 4 (OE-315): per-person access. Pure and side-effect free; the derived-data
 * script writes what these return, and stage 5's backend shares them.
 *
 * The Firestore rules (firestore.rules, `salesPerformance`) decide from `access/{uid}`; these functions build
 * that document, the per-rep month totals a limited person reads in place of the month doc, and the per-rep
 * customer names that stand in for the company-wide `catalog/customers`.
 */
import { RepMonth } from './sales-performance';
import { OrgPartLike, SpPeople } from './sales-performance-people';
import { BandCounts } from './sales-performance-segments';

/** access/{uid}: what the rules read. `reps` are IQ rep numbers as strings. */
export interface SpAccessDoc {
    company: boolean;
    manage: boolean;
    reps: string[];
}

export const SP_FEATURE = 'sales-performance';
export const SP_VIEW_COMPANY = 'view-company-sales-performance';
export const SP_MANAGE = 'manage-sales-performance';

/** An org member doc (organizations/{org}/users/{uid}), as far as this reads it. */
export interface SpMemberLike {
    uid: string;
    features?: string[];
    featureDetails?: Record<string, { permissions?: string[] } | undefined>;
}

/**
 * company / manage from the member's grants, the same permissions SpContext reads. null = the member does not hold
 * the Sales Performance feature at all, so no access document is written for them.
 */
export function grantsOf(member: SpMemberLike): { company: boolean; manage: boolean } | null {
    if (!(member.features || []).includes(SP_FEATURE)) return null;
    const perms = member.featureDetails?.[SP_FEATURE]?.permissions || [];
    return { company: perms.includes(SP_VIEW_COMPANY), manage: perms.includes(SP_MANAGE) };
}

/**
 * The reps a person may read. Their own (every rep number mapped to them), plus, when one of their posts is a
 * manager or I/C post, every rep whose post sits under it. A manager post with nobody beneath it (an I/C who
 * sits among their staff) covers the reps of their own team instead. Identity is the partKey, never the id.
 */
export function repsFor(uid: string, people: SpPeople, orgParts: OrgPartLike[]): string[] {
    const byKey = new Map<string, OrgPartLike>();
    const byPath = new Map<string, OrgPartLike>();
    for (const p of orgParts || []) {
        if (!p?.partKey) continue;
        byKey.set(p.partKey, p);
        if (p.path) byPath.set(p.path, p);
    }
    const parentOf = (p: OrgPartLike) =>
        (p.parentPartKey && byKey.get(p.parentPartKey)) || (p.parentRef ? byPath.get(p.parentRef) : undefined);
    /** the keys of a post's ancestors; a cycle in bad data ends the walk */
    const ancestorKeys = (partKey: string): Set<string> => {
        const out = new Set<string>();
        const start = byKey.get(partKey);
        for (let c = start && parentOf(start); c && !out.has(c.partKey) && c.partKey !== partKey; c = parentOf(c))
            out.add(c.partKey);
        return out;
    };

    const entries = Object.entries(people);
    const mine = entries.filter(([, p]) => p.uid === uid);
    const reps = new Set(mine.map(([rep]) => rep));
    for (const [, me] of mine) {
        if (me.kind !== 'manager' || !me.post) continue;
        const below = entries.filter(
            ([, p]) => p.post && p.post.key !== me.post!.key && ancestorKeys(p.post.key).has(me.post!.key)
        );
        const covered = below.length
            ? below
            : entries.filter(([, p]) => p.post && me.team && p.team?.key === me.team.key);
        for (const [rep] of covered) reps.add(rep);
    }
    return [...reps].sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));
}

/**
 * The access documents to write: one per member who holds the feature. Developers are left out (the rules and
 * the page give them everything). A member can hold the feature and have no rep yet: then `reps` is empty and the
 * page says their figures are not linked.
 */
export function buildAccess(input: {
    members: (SpMemberLike & { isDeveloper?: boolean })[];
    /** The org doc's `owners`: they hold company and manage access whatever their grants say (the rules agree). */
    owners?: string[];
    people: SpPeople;
    orgParts: OrgPartLike[];
}): Record<string, SpAccessDoc> {
    const out: Record<string, SpAccessDoc> = {};
    const developers = new Set(input.members.filter((m) => m.isDeveloper).map((m) => m.uid));
    const owners = new Set((input.owners || []).filter((o) => typeof o === 'string' && o));
    for (const m of input.members) {
        if (m.isDeveloper) continue;
        const g = owners.has(m.uid) ? { company: true, manage: true } : grantsOf(m);
        if (!g) continue;
        out[m.uid] = { company: g.company, manage: g.manage, reps: repsFor(m.uid, input.people, input.orgParts) };
    }
    // an owner with no member doc of their own still reads everything
    for (const uid of owners)
        if (!out[uid] && !developers.has(uid))
            out[uid] = { company: true, manage: true, reps: repsFor(uid, input.people, input.orgParts) };
    return out;
}

/** The uids that hold an access document but no longer should: to delete. */
export function staleAccess(existing: string[], wanted: Record<string, SpAccessDoc>): string[] {
    return existing.filter((uid) => !wanted[uid]);
}

/** cycles/{k}/months/{m}/repTotals/{rep}: one salesperson's month, plus the band mix their weighted targets need. */
export interface RepTotalsDoc extends RepMonth {
    rep: string;
    month: string;
    /** active customers per customer-size band that month */
    mix: BandCounts;
}

/** One doc per salesperson in the month. `mixes` is segments.mix[month]. A rep with no mix gets an empty one. */
export function buildRepTotals(
    month: string,
    reps: Record<string, RepMonth>,
    mixes: Record<string, BandCounts> | undefined
): Record<string, RepTotalsDoc> {
    return Object.fromEntries(
        Object.entries(reps || {}).map(([rep, r]) => [rep, { ...r, rep, month, mix: mixes?.[rep] || {} }])
    );
}

/** customerNames/{rep}: the names of one salesperson's customers, from the customer lists of every month given. */
export interface CustomerNamesDoc {
    rep: string;
    names: Record<string, string>;
}

/**
 * `lists` is month -> rep -> account -> anything; `names` the company-wide account -> name map. A customer with
 * no name is left out (the page falls back to the account code). Only the rep's own customers are included.
 */
export function buildCustomerNames(
    lists: Record<string, Record<string, Record<string, unknown>>>,
    names: Record<string, string>
): Record<string, CustomerNamesDoc> {
    const out: Record<string, CustomerNamesDoc> = {};
    for (const perRep of Object.values(lists || {}))
        for (const [rep, customers] of Object.entries(perRep || {})) {
            const doc = (out[rep] = out[rep] || { rep, names: {} });
            for (const code of Object.keys(customers || {})) if (names[code]) doc.names[code] = names[code];
        }
    return out;
}
